import React from "react";
import { Box, Container, Paper, Typography, Tooltip, IconButton, alpha, CircularProgress } from "@mui/material";
import { RestartAlt as RestartIcon } from "@mui/icons-material";
import { useDevicesApi } from "../utlis/useDevicesApi";
import axiosInstance from "../utlis/axiosInstance";

const DisabledDevices: React.FC = () => {
  const { devices, loading, refreshDevices } = useDevicesApi();

  const disabledDevices = React.useMemo(
    () => devices.filter((d: any) => d.disabled === true),
    [devices]
  );

  const [updatingId, setUpdatingId] = React.useState<number | null>(null);

  const handleRestart = async (deviceId: number) => {
    try {
      setUpdatingId(deviceId);
      const res = await axiosInstance.put(`/gpstracker/devicehandler/${deviceId}`, {
        disabled: false,
      });
      if (res.data?.status) {
        await refreshDevices();
      }
    } catch (e) {
      console.error("Failed to enable device", e);
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="xl">
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={700} color="#111827">
            Disabled Devices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Devices that are currently disabled. Click Restart to re-enable.
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid",
            borderColor: "#e2e8f0",
            overflow: "hidden",
            bgcolor: "white",
          }}
        >
          <Box sx={{ overflowX: "auto" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "60px 1fr 140px 140px 100px",
                gap: 1,
                px: 1.5,
                py: 1,
                bgcolor: alpha("#1976d2", 0.05),
                borderBottom: `1px solid ${alpha("#1976d2", 0.2)}`,
                alignItems: "center",
                minHeight: 40,
                width: "100%",
              }}
            >
              <Typography variant="caption" fontWeight={600} color="#374151" sx={{ fontSize: "0.7rem", textAlign: "center" }}>#</Typography>
              <Typography variant="caption" fontWeight={600} color="#374151" sx={{ fontSize: "0.7rem" }}>VEHICLE DETAILS</Typography>
              <Typography variant="caption" fontWeight={600} color="#374151" sx={{ fontSize: "0.7rem" }}>MODEL</Typography>
              <Typography variant="caption" fontWeight={600} color="#374151" sx={{ fontSize: "0.7rem" }}>MDN</Typography>
              <Typography variant="caption" fontWeight={600} color="#374151" sx={{ fontSize: "0.7rem", textAlign: "center" }}>ACTION</Typography>
            </Box>

            <Box>
              {disabledDevices.map((device: any, index: number) => (
                <Box
                  key={device.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "60px 1fr 140px 140px 100px",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    alignItems: "center",
                    minHeight: 48,
                    borderBottom: `1px solid ${alpha("#1976d2", 0.1)}`,
                    width: "100%",
                  }}
                >
                  <Typography variant="caption" color="#6b7280" fontWeight={500} sx={{ fontSize: "0.7rem", textAlign: "center" }}>
                    {index + 1}
                  </Typography>

                  <Box>
                    <Typography variant="caption" fontWeight={600} color="#1f2937" sx={{ fontSize: "0.7rem", mb: 0.2, display: "block" }}>
                      {device.name}
                    </Typography>
                    <Typography variant="caption" color="#6b7280" sx={{ fontSize: "0.65rem", display: "block", mb: 0.2 }}>
                      ID: {device.unique_id}
                    </Typography>
                    <Typography variant="caption" color="#6b7280" sx={{ fontSize: "0.65rem", display: "block" }}>
                      SIM No.: {device.sim_number || "-"}
                    </Typography>
                  </Box>

                  <Typography variant="caption" color="#374151" fontWeight={500} sx={{ fontSize: "0.7rem" }}>
                    {device.model || "N/A"}
                  </Typography>

                  <Typography variant="caption" color="#374151" fontWeight={500} sx={{ fontSize: "0.7rem" }}>
                    {device.phone || "N/A"}
                  </Typography>

                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Tooltip title="Restart (Enable)" arrow>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleRestart(device.id)}
                          disabled={updatingId === device.id}
                          sx={{
                            width: 24,
                            height: 24,
                            background: `linear-gradient(135deg, ${alpha("#43A047", 0.1)} 0%, ${alpha("#43A047", 0.05)} 100%)`,
                            border: "1px solid",
                            borderColor: alpha("#43A047", 0.3),
                            color: "#43A047",
                            "&:hover": {
                              background: `linear-gradient(135deg, ${alpha("#43A047", 0.2)} 0%, ${alpha("#43A047", 0.1)} 100%)`,
                              transform: "scale(1.05)",
                            },
                          }}
                        >
                          {updatingId === device.id ? (
                            <CircularProgress size={12} color="inherit" />
                          ) : (
                            <RestartIcon sx={{ fontSize: 14 }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              ))}

              {disabledDevices.length === 0 && !loading && (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography variant="h6" color="text.secondary" fontWeight={600}>
                    No disabled devices
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All devices are currently enabled.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default DisabledDevices;


